import type { TFunction } from 'i18next';
import numeral from 'numeral';
import type { JSX } from 'react';
import React from 'react';
import { Link } from 'react-router-dom';
import type { ColumnRenderProps } from 'react-table';

import type { IColumn, TTheme } from '@feathr/components';
import { ContextMenu, TableColumnHeader, Text, Time } from '@feathr/components';
import { getIconForAction, TimeFormat } from '@feathr/hooks';

import DemoMenuItem from '../components/DemoMenuItem';
import type { ISampleDonationTransaction } from '../sampleData';
import buildOptionsColumn from './buildOptionsColumn';
import buildPillColumn from './buildPillColumn';

import * as tableStyles from '@feathr/components/dist/Table/Table.css';

const STATUS_LABEL: Record<ISampleDonationTransaction['status'], string> = {
  completed: 'Completed',
  pending: 'Pending',
  refunded: 'Refunded',
};

const STATUS_THEME: Record<ISampleDonationTransaction['status'], TTheme> = {
  completed: 'green',
  pending: 'neutral',
  refunded: 'red',
};

interface IDonationTransactionColumnsArgs {
  base: string;
  t: TFunction;
}

function donationTransactionColumns({
  base,
  t,
}: IDonationTransactionColumnsArgs): Array<IColumn<ISampleDonationTransaction>> {
  return [
    {
      accessor: 'donor',
      id: 'donor',
      Header: TableColumnHeader({ title: t('Donor'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      minWidth: 200,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.donor}</Link>;
      },
      Footer(): JSX.Element {
        return <strong>{t('Page totals')}</strong>;
      },
    },
    {
      accessor: 'amount',
      id: 'amount',
      Header: TableColumnHeader({ title: t('Amount'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 120,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.amount).format('$0,0.00')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleDonationTransaction }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.amount, 0);
        return <>{numeral(total).format('$0,0.00')}</>;
      },
    },
    {
      accessor: 'growthCredits',
      id: 'growthCredits',
      Header: TableColumnHeader({ title: t('Growth Credits'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellRight,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        return <>{numeral(original.growthCredits).format('0,0')}</>;
      },
      Footer({ data }: ColumnRenderProps<{ _original: ISampleDonationTransaction }>): JSX.Element {
        const total = data.reduce((subtotal, row) => subtotal + row._original.growthCredits, 0);
        return <>{numeral(total).format('0,0')}</>;
      },
    },
    {
      accessor: 'formName',
      id: 'formName',
      Header: TableColumnHeader({ title: t('Form'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      Cell({ original }): JSX.Element {
        return <Link to={base}>{original.formName}</Link>;
      },
    },
    {
      accessor: (row): string => row.campaign ?? '',
      id: 'campaign',
      Header: TableColumnHeader({ title: t('Campaign'), sortType: 'alpha' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cell,
      sortable: true,
      Cell({ original }): JSX.Element {
        if (!original.campaign) {
          return <Text variant={'smallRegular'}>{t('—')}</Text>;
        }
        return <Link to={base}>{original.campaign}</Link>;
      },
    },
    buildPillColumn<ISampleDonationTransaction, ISampleDonationTransaction['status']>({
      accessor: 'status',
      labelMap: STATUS_LABEL,
      t,
      themeMap: STATUS_THEME,
      title: t('Status'),
      width: 120,
    }),
    {
      accessor: 'date',
      id: 'date',
      Header: TableColumnHeader({ title: t('Date'), sortType: 'numeric' }),
      headerClassName: tableStyles.sort,
      className: tableStyles.cellCenter,
      sortable: true,
      width: 140,
      Cell({ original }): JSX.Element {
        return <Time format={TimeFormat.shortDate} timestamp={original.date} />;
      },
    },
    buildOptionsColumn<ISampleDonationTransaction>({
      t,
      renderItems: (original): JSX.Element => (
        <>
          <ContextMenu.Item link={base} prefix={getIconForAction('view')}>
            {t('View receipt')}
          </ContextMenu.Item>
          {original.status === 'completed' && (
            <DemoMenuItem action={'return'} isDestructive={true} label={t('Refund')} />
          )}
        </>
      ),
    }),
  ];
}

export default donationTransactionColumns;
